import type { ReactNode } from "react"
import { Box, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { ErrorBoundary } from "react-error-boundary"

import type { MeetingRowOrder } from "../../../weekMenuTypes"
import { UserDashboardErrorFallbackCard } from "../../UserDashboardErrorFallbackCard"

import type { MeetingDayGroup } from "./meetingDayGroups"
import { MeetingRow } from "./MeetingRow"

const useStyles = makeStyles()((theme) => ({
    rowsContainer: {
        display: "flex",
        flexDirection: "column",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    dayLabel: {
        color: theme.palette.tailwind.slate[500],
        fontSize: "1.25rem",
        fontWeight: 500,
        marginBottom: ".5rem",
        "&:not(:first-of-type)": {
            marginTop: "2rem",
        },
    },
    dayMeta: {
        marginLeft: ".625rem",
        color: theme.palette.tailwind.slate[400],
    },
    errorCard: {
        backgroundColor: "transparent",
        boxShadow: "none",
    },
    errorContent: {
        paddingTop: 0,
        paddingLeft: 0,
        paddingRight: 0,
    },
}))

type MeetingsDayListProps<T extends MeetingRowOrder> = Readonly<{
    groups: MeetingDayGroup<T>[]
    meetings: T[]
    renderGroupMeta?: (group: MeetingDayGroup<T>) => ReactNode
    renderTitleTags?: (meeting: T) => ReactNode
}>

export function MeetingsDayList<T extends MeetingRowOrder>({
    groups,
    meetings,
    renderGroupMeta,
    renderTitleTags,
}: MeetingsDayListProps<T>) {
    const { classes } = useStyles()

    return (
        <Box className={classes.rowsContainer}>
            {groups.flatMap((group) => [
                <Typography key={`label-${group.key}`} variant="h6" className={classes.dayLabel}>
                    {group.label}
                    {renderGroupMeta && (
                        <Typography variant="body2" component="span" className={classes.dayMeta}>
                            {renderGroupMeta(group)}
                        </Typography>
                    )}
                </Typography>,
                ...group.meetings.map((meeting) => (
                    <ErrorBoundary
                        key={meeting.id}
                        fallback={
                            <UserDashboardErrorFallbackCard
                                classNames={classes.errorCard}
                                contentClassNames={classes.errorContent}
                            />
                        }
                    >
                        <MeetingRow meeting={meeting} meetings={meetings} titleTags={renderTitleTags?.(meeting)} />
                    </ErrorBoundary>
                )),
            ])}
        </Box>
    )
}
